/**
 * EPIC-DM01-G: Share Modal Component (Redesigned)
 * Simplified to 4 option cards matching Figma
 *
 * Design Reference: Figma Share Modal (phone)
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { useDemoShare } from '@/hooks/useDemoShare';
import {
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  MessageSquare,
  Share2,
  ShieldAlert,
  X,
} from 'lucide-react';
import { DemoButton } from '@/components/demo/ui';

export interface ShareModalProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  onEmailReport?: () => void;
  initialPreview?: {
    insight?: string;
    focusArea?: string;
    overallScore?: number;
  };
}

type ShareAction = 'email' | 'text' | 'copy' | 'download';

interface ShareOption {
  id: ShareAction;
  icon: typeof Mail;
  label: string;
  description: string;
}

const SHARE_OPTIONS: ShareOption[] = [
  {
    id: 'email',
    icon: Mail,
    label: 'Email Report',
    description: 'Send the full analysis report to your inbox',
  },
  {
    id: 'text',
    icon: MessageSquare,
    label: 'Text Message',
    description: 'Get a share link sent via SMS',
  },
  {
    id: 'copy',
    icon: Copy,
    label: 'Copy Link',
    description: 'Share the link via any app',
  },
  {
    id: 'download',
    icon: Share2,
    label: 'View Full Report',
    description: 'Open the full analysis in a new tab',
  },
];

export function ShareModal({
  sessionId,
  isOpen,
  onClose,
  onEmailReport,
  initialPreview,
}: ShareModalProps) {
  const [actionInProgress, setActionInProgress] = useState<ShareAction | null>(null);
  const [actionSuccess, setActionSuccess] = useState<ShareAction | null>(null);

  const {
    isGenerating,
    shareUrl,
    error,
    errorReason,
    hasNativeShare,
    generateShareCard,
    copyShareUrl,
    nativeShare,
    clearError,
  } = useDemoShare(sessionId);

  const isBlocked = error && (errorReason === 'user_not_opted_in' || errorReason === 'system_sharing_disabled');

  const showSuccess = (action: ShareAction) => {
    setActionSuccess(action);
    setTimeout(() => setActionSuccess(null), 2000);
  };

  const handleAction = async (action: ShareAction) => {
    setActionInProgress(action);
    clearError();

    try {
      switch (action) {
        case 'email':
          if (onEmailReport) {
            onEmailReport();
          }
          onClose();
          break;

        case 'text':
          if (!shareUrl) {
            await generateShareCard('1:1');
          }
          if (hasNativeShare) {
            await nativeShare();
          } else {
            const url = shareUrl || window.location.href;
            window.open(`sms:?body=${encodeURIComponent(`Check out my swing analysis: ${url}`)}`, '_self');
          }
          showSuccess('text');
          break;

        case 'copy':
          if (!shareUrl) {
            await generateShareCard('1:1');
          }
          const copied = await copyShareUrl();
          if (copied) {
            showSuccess('copy');
          }
          break;

        case 'download': {
          const resultsUrl = `${window.location.origin}/demo/results/${sessionId}`;
          window.open(resultsUrl, '_blank');
          showSuccess('download');
          break;
        }
      }
    } catch (err) {
      console.error(`[ShareModal] ${action} failed:`, err);
    } finally {
      setActionInProgress(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="demo-mode sm:max-w-md p-0 gap-0 bg-demo-surface rounded-2xl overflow-hidden border-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-demo-green" />
            <h2 className="text-lg font-bold text-demo-text">Share Your Results</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full text-demo-text-muted hover:bg-demo-surface-alt"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 pb-6 pt-2">
          {/* Blocked state */}
          {isBlocked && (
            <div className="mb-4 p-4 bg-demo-error-light rounded-xl">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-demo-error shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-demo-error text-sm">Sharing Not Available</p>
                  <p className="text-xs text-demo-error/80 mt-1">
                    {errorReason === 'user_not_opted_in'
                      ? 'Social sharing was not enabled during intake.'
                      : 'Social sharing is currently disabled.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Option cards */}
          <div className="space-y-2">
            {SHARE_OPTIONS.map((option) => {
              const isLoading = actionInProgress === option.id;
              const isSuccess = actionSuccess === option.id;
              const isDisabled = !!actionInProgress || !!isBlocked;
              const Icon = option.icon;

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleAction(option.id)}
                  disabled={isDisabled}
                  className={`
                    w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left
                    ${isSuccess
                      ? 'bg-demo-green-light border-demo-green/30'
                      : 'bg-white border-gray-100 hover:border-demo-green/30 hover:shadow-demo-sm'
                    }
                    ${isDisabled && !isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-[0.98]'}
                  `}
                >
                  <div className={`
                    flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                    ${isSuccess ? 'bg-demo-green' : 'bg-demo-surface-alt'}
                  `}>
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-demo-green" />
                    ) : isSuccess ? (
                      <Check className="h-5 w-5 text-white" />
                    ) : (
                      <Icon className="h-5 w-5 text-demo-text-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${isSuccess ? 'text-demo-green-darkest' : 'text-demo-text'}`}>
                      {isSuccess ? 'Done!' : option.label}
                    </p>
                    <p className="text-xs text-demo-text-muted truncate">{option.description}</p>
                  </div>
                  {!isLoading && !isSuccess && (
                    <ChevronRight className="h-5 w-5 text-demo-text-muted flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Cancel button */}
          <div className="mt-4">
            <DemoButton variant="ghost" fullWidth onClick={onClose}>
              Cancel
            </DemoButton>
          </div>

          {/* Privacy note */}
          <p className="text-center text-xs text-demo-text-muted mt-3">
            No personal information is shared. Links expire in 7 days.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
